import Observer from '@/common/scripts/observer';
import {Utils} from '@/common/scripts/utils';

const observer = new Observer();

interface IState {
    activeView: HTMLElement | null;
    mainWrapper: HTMLElement;
    sunWrapper: HTMLElement | null;
    views: HTMLElement | null;
    size: number;
    sunRotateButtons: HTMLElement[] | null;
    fullScreenOpen: boolean;
    switchers: {
        wrapper: HTMLElement | null;
        sun: HTMLInputElement | null;
        furniture: HTMLInputElement | null;
        fullScreen: HTMLElement | null;
    };
    elements: {
        sun: HTMLElement | null;
        circle: HTMLElement | null;
        rotate: HTMLElement | null;
    };
}

const state: IState = {
    activeView      : null,
    mainWrapper     : document.body,
    sunWrapper      : null,
    views           : null,
    size            : 0,
    sunRotateButtons: null,
    fullScreenOpen  : false,
    switchers       : {
        wrapper   : null,
        sun       : null,
        furniture : null,
        fullScreen: null
    },
    elements: {
        sun   : null,
        circle: null,
        rotate: null
    }
};

const HIDE_CLASS = 'is-hide';
// Модификатор активного класса
const ACTIVE_CLASS = 'is-active';
// Модификатор открытого на весь экран вида
const FULL_CLASS = 'is-full';


/**
 * Показывать или нет - переключатели
 * @param {boolean} haveSwitchers - показывать или скрывать переключатели
 * @param {boolean} fullScreen - взаимодействие с полноэкранным режимом
 */
const showSwitchers = (haveSwitchers: boolean, fullScreen: string = 'true'): void => {
    const method = haveSwitchers ? 'remove' : 'add';
    // Если аттрибут false - показываем класс скрывающий кнопку раскрытия на весь экран
    const fullMethod = fullScreen === 'false' ? 'add' : 'remove';

    state.switchers.sun?.parentElement?.classList[method](HIDE_CLASS);
    state.switchers.furniture?.parentElement?.classList[method](HIDE_CLASS);
    state.switchers.fullScreen?.classList[fullMethod](HIDE_CLASS);
};

/**
 * Установка размеров орбиты солнца и положение иконки солнца
 */
const setSunStyles = (): void => {
    if (!state.elements.sun || !state.sunWrapper) {
        return;
    }

    const activeDegElement = state.sunRotateButtons?.find((element: HTMLElement) => {
        return element.classList.contains(ACTIVE_CLASS);
    });
    const deg = activeDegElement?.dataset?.['degree'] || null;
    let rotate: string | number = 0;

    if (deg) {
        rotate = deg;
    } else if (state.sunWrapper.dataset?.['sunDefault']) {
        rotate = state.sunWrapper.dataset?.['sunDefault'] || 45;
    }

    const width = Number(state.sunWrapper.parentElement?.clientWidth);
    const height = Number(state.sunWrapper.parentElement?.clientHeight);

    state.size =
        height <= width ?
            height :
            width === document.body.offsetWidth ? width * 0.85 : width;
    const sunSize = state.elements.sun.clientWidth;

    state.sunWrapper.style.width = `${state.size}px`;
    state.sunWrapper.style.height = `${state.size}px`;
    state.sunWrapper.style.padding = '0';

    state.elements.sun.style.top = `${(state.size - sunSize) / 2}px`;
    state.elements.sun.style.left = `${(state.size - sunSize) / 2}px`;
    state.elements.sun.style.transform = `rotate(${rotate}deg) translateY(${state.size / 2}px)`;
};

const sunRotateEvent = (event: Event): void => {
    if (event.target instanceof HTMLElement) {
        const targetButton = event.target.closest('button');
        const degree = targetButton?.dataset?.['degree'];

        if (!state.elements.sun || targetButton?.classList.contains(ACTIVE_CLASS)) {
            return;
        }

        const activeButton = state.sunRotateButtons?.find((button: HTMLElement) => {
            return button.classList.contains(ACTIVE_CLASS);
        });

        activeButton?.classList.remove(ACTIVE_CLASS);
        targetButton?.classList.add(ACTIVE_CLASS);

        state.elements.sun.style.transform = `rotate(${degree}deg) translateY(${state.size / 2}px)`;
    }
};

/**
 * Обработка кликов по свитчеру мебели
 * @param {HTMLElement} element - изображение с мебелью
 */
const switcherFurnitureListener = (element: HTMLElement | null): void => {
    if (!state.switchers.furniture || !element) {
        return;
    }

    state.switchers.furniture.addEventListener('click', () => {
        state.switchers.furniture?.checked ?
            element.classList.remove(HIDE_CLASS) :
            element.classList.add(HIDE_CLASS);
    });
};

/**
 * Обработка клика по свитчеру солнца
 */
const switcherSunListener = (): void => {
    if (!state.switchers.sun || !state.sunWrapper) {
        return;
    }

    if (!state.elements.sun) {
        state.elements.sun = state.sunWrapper.querySelector('.j-sun');
    }

    const images: HTMLElement[] = [...state.sunWrapper.querySelectorAll('img')];

    state.elements.circle = state.sunWrapper.querySelector('.j-sun-circle');
    state.elements.rotate = state.mainWrapper?.querySelector('.j-flat-card-v2__sun-rotate') || null;

    state.sunRotateButtons = state.elements.rotate ?
        [...state.elements.rotate.querySelectorAll('button')] :
        null;

    if (!state.elements.sun || !state.elements.circle) {
        return;
    }

    setSunStyles();

    state.switchers.sun.addEventListener('click', () => {
        const sunMethod = state.switchers.sun?.checked ? 'remove' : 'add';
        const imagesMethod = state.switchers.sun?.checked ? 'add' : 'remove';
        const buttonMethod = state.switchers.sun?.checked ? 'addEventListener' : 'removeEventListener';

        state.activeView?.classList[imagesMethod]('is-sun-open');

        Object.keys(state.elements)?.forEach((element: string): void => {
            state.elements[element]?.classList[sunMethod](HIDE_CLASS);
        });

        images.forEach((image: HTMLElement) => {
            image.classList[imagesMethod]('is-small');
        });

        state.sunRotateButtons?.forEach((button: HTMLElement) => {
            button[buttonMethod]('click', sunRotateEvent, true);
        });
    });
};

/**
 * Переключение полноэкранного режима
 */
const switcherFullScreenListener = (): void => {
    if (!state.switchers.fullScreen) {
        return;
    }

    state.switchers.fullScreen.addEventListener('click', () => {
        const method = state.fullScreenOpen ? 'remove' : 'add';

        state.activeView?.classList[method](FULL_CLASS);
        state.switchers.wrapper?.classList[method](FULL_CLASS);
        state.switchers.fullScreen?.classList[method](ACTIVE_CLASS);

        state.elements.rotate?.classList[method](FULL_CLASS);

        setSunStyles();

        state.fullScreenOpen ? Utils.bodyStatic() : Utils.bodyFixed(state.mainWrapper);

        state.fullScreenOpen = !state.fullScreenOpen;
    });
};

const flatCardV2 = (wrapper: HTMLElement): void => {
    state.mainWrapper = wrapper;
    state.views = state.mainWrapper?.querySelector('.j-flat-card-v2-views');
    state.switchers.wrapper = state.views?.querySelector('.j-flat-card-v2-switchers') || null;
    state.switchers.furniture = state.switchers.wrapper?.querySelector('.j-flat-furniture') || null;
    state.switchers.sun = state.switchers.wrapper?.querySelector('.j-flat-sun') || null;
    state.switchers.fullScreen = state.switchers.wrapper?.querySelector('.j-flat-fullscreen') || null;
    state.sunWrapper = state.views?.querySelector('.j-sun-image-wrapper') || null;
    state.activeView =
        state.mainWrapper?.querySelector(`.j-tabs-content__item.${ACTIVE_CLASS}`) || null;

    const withFurniture: HTMLElement | null = state.views?.querySelector('.j-with-furniture') || null;

    observer.subscribe('tabChange', (element: HTMLElement): void => {
        const activeTab = element.dataset['tab'];

        state.activeView = state.views?.querySelector(`.j-tabs-content__item[data-tab="${activeTab}"]`) || null;
        showSwitchers(Boolean(element.dataset?.['haveSwitchers']), element.dataset?.['fullscreen']);
    });

    switcherFurnitureListener(withFurniture);
    switcherFullScreenListener();

    if (!state.sunWrapper) {
        return;
    }

    switcherSunListener();

    const events = ['resize', 'orientationchange'];

    events.forEach((event: string) => {
        window.addEventListener(event, () => {
            setSunStyles();
        });
    });
};

export default flatCardV2;
